import { EditIcon, LockIcon, UnlockIcon } from '@chakra-ui/icons'
import { Box, Button, Center, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { useFirebase } from '@chem-po/firebase-web'
import {
  CheckboxField,
  ConsentFormTemplate,
  FieldTypes,
  getTemplateCollectionPath,
  PracticeConsentForm,
  WithId,
} from '@hb/shared'
import { doc } from 'firebase/firestore'
import { useCallback, useState } from 'react'
import { useUpdateDoc } from '../../../hooks/backend/useUpdateDoc'
import { SolidActionButton } from '../../Buttons/ActionButton'
import { Editable } from '../../forms/Input'
import { AutoDisplayConditionsView } from './AutoDisplayCondition'

const canBeCopiedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Can be copied to a practice?',
  yesText: 'Can be copied to a practice',
  noText: 'Cannot be copied to a practice',
  inputType: 'switch',
}

const IsInternalView = ({
  consentFormTemplate,
}: {
  consentFormTemplate: WithId<ConsentFormTemplate>
}) => {
  const update = useUpdateDoc('template permissions')
  const { firestore } = useFirebase()
  const [isLoading, setIsLoading] = useState(false)
  const handleChange = useCallback(
    async (value: boolean) => {
      setIsLoading(true)
      const ref = doc(
        firestore,
        `${getTemplateCollectionPath(null, 'consentForm')}/${consentFormTemplate.id}`,
      )
      await update(ref, 'isInternal', value)

      setIsLoading(false)
    },
    [consentFormTemplate, firestore, update],
  )
  return (
    <Flex w="100%" borderTop="1px solid" borderColor="gray.200" pt={2} px={2} mt={2}>
      <Button
        variant="ghost"
        size="sm"
        isLoading={isLoading}
        gap={2}
        onClick={() => handleChange(!consentFormTemplate.isInternal)}>
        {consentFormTemplate.isInternal ? (
          <LockIcon color="#f5c802" />
        ) : (
          <UnlockIcon color="gray.400" />
        )}
        <Text fontWeight={400} fontSize="sm" fontFamily="Hero-New">
          {consentFormTemplate.isInternal
            ? 'Internal use (Cannot be sent)'
            : 'Patient use (Can be sent)'}
        </Text>
      </Button>
    </Flex>
  )
}

const CanBeCopiedView = ({
  consentFormTemplate,
}: {
  consentFormTemplate: WithId<ConsentFormTemplate>
}) => {
  const update = useUpdateDoc('template permissions')
  const { firestore } = useFirebase()
  const handleChange = useCallback(
    (value: boolean) => {
      const ref = doc(
        firestore,
        `${getTemplateCollectionPath(null, 'consentForm')}/${consentFormTemplate.id}`,
      )
      return update(ref, 'canBeCopiedToPractice', value)
    },
    [consentFormTemplate, firestore, update],
  )
  return (
    <Box w="100%" borderTop="1px solid" borderColor="gray.200" pt={1} mt={2}>
      <Editable
        theme="detailed"
        field={canBeCopiedField}
        value={consentFormTemplate.canBeCopiedToPractice}
        onSubmit={handleChange}
      />
    </Box>
  )
}

export const ConsentFormPreview = <T extends PracticeConsentForm | ConsentFormTemplate>({
  consentForm,
  onOpenAutoDisplay,
  onOpenTemplate,
  openText = 'Edit Template',
  practiceId,
}: {
  consentForm: WithId<T>
  onOpenAutoDisplay?: () => void
  onOpenTemplate?: () => void
  practiceId: string | null
  openText?: string
}) => (
  <VStack py={1} w="100%" mb={1} fontFamily="Hero-New" align="flex-start" borderColor="gray.200">
    <HStack spacing={0} w="100%">
      <Box flex={1} minW="0" borderRight="1px solid" borderColor="gray.200">
        <AutoDisplayConditionsView
          conditions={consentForm?.autoDisplay}
          onOpen={onOpenAutoDisplay}
        />
        {practiceId ? null : <CanBeCopiedView consentFormTemplate={consentForm} />}
        <IsInternalView consentFormTemplate={consentForm} />
      </Box>
      <Center px={3} flexFlow="column">
        <Text fontSize="md" fontFamily="Open Sans" color="gray.600">
          Template
        </Text>
        <SolidActionButton gap={2} size="sm" onClick={onOpenTemplate}>
          <EditIcon filter="drop-shadow(1px 1px 3px #00000077)" />
          <Text>{openText}</Text>
        </SolidActionButton>
      </Center>
    </HStack>
  </VStack>
)
